import _ from "lodash";
import { v4 } from "uuid";
import Form from "@rjsf/core";
import {
  MarketplaceForm,
  MarketplaceFormConfig,
  MarketplaceFormElement,
  MarketplaceFormElementRole,
  MarketplaceFormElementType,
  MarketplaceFormElementWidget,
  MarketplaceFormIcon,
  MarketplaceOptionGridType,
  MarketplaceOptionType,
} from "../../schema/generated/schema";
import schemaGenerator from "./schemaGenerator";

export const defaultPropsForElements = {
  key: "",
  type: MarketplaceFormElementType.Text,
  role: MarketplaceFormElementRole.None,
  label: "",
  tooltip: "",
  defaultValue: null,
  description: "",
  required: false,
  widget: null,
  validation: null,
  grid: MarketplaceOptionGridType.Auto,
  // object
  buyerAccess: { editable: true, hidden: false },
  // object
  sellerAccess: { editable: true, hidden: false },
  clearWhenChanged: [],
  // object
  optionValues: [],
  optionType: null,
  // special object
  dateOptionValues: "",
  optionSourceExternalId: null,
  min: null,
  max: null,
  regex: null,
  allowedFileExtensions: null,
  hideIf: null,
  placeholder: null,
};

const enumToOptions = (enumObj: any) => {
  return _.map(enumObj, (value, label) => {
    return { value, label: _.startCase(label) };
  });
};

export const elementToFormData = (element: MarketplaceFormElement): any => {
  const fromObj = (obj: any) => Object.keys(obj).filter(k => obj[k]);
  // eslint-disable-next-line prefer-destructuring
  let optionValues: any = element.optionValues;
  if (optionValues) {
    optionValues = JSON.stringify(optionValues, null, 2);
  }
  return {
    ...element,
    optionValues,
    buyerAccess: fromObj(element.buyerAccess),
    sellerAccess: fromObj(element.sellerAccess),
  };
};

export const elementFromFormData = (
  formData: any,
  element?: any
): MarketplaceFormElement => {
  const toObj = (arr: string[]) =>
    arr.reduce(
      (memo, key) => {
        return {
          ...memo,
          [key]: true,
        };
      },
      {
        editable: false,
        hidden: false,
      }
    );
  let optionValues = null;
  if (formData.optionValues) {
    try {
      optionValues = JSON.parse(formData.optionValues);
    } catch (err) {
      console.error("problem json parsing option values");
      optionValues = element.optionValues;
    }
  }
  return {
    ...formData,
    optionValues,
    buyerAccess: formData.buyerAccess
      ? toObj(formData.buyerAccess)
      : element?.buyerAccess,
    sellerAccess: formData.sellerAccess
      ? toObj(formData.sellerAccess)
      : element?.sellerAccess,
  };
};

const partialElementConfig: { [key: string]: Partial<MarketplaceFormElement> } =
  {
    type: {
      type: MarketplaceFormElementType.Select,
      optionType: MarketplaceOptionType.Text,
      optionValues: enumToOptions(MarketplaceFormElementType),
      required: true,
      clearWhenChanged: ["regex", "widget", "grid", "min", "max", "validation"],
    },
    key: {
      type: MarketplaceFormElementType.Text,
      required: true,
    },
    required: {
      type: MarketplaceFormElementType.Bool,
    },
    buyerAccess: {
      type: MarketplaceFormElementType.MultiSelect,
      widget: MarketplaceFormElementWidget.Checkboxes,
      optionValues: ["hidden", "editable"],
      defaultValue: ["editable"],
    },
    sellerAccess: {
      type: MarketplaceFormElementType.MultiSelect,
      widget: MarketplaceFormElementWidget.Checkboxes,
      optionValues: ["hidden", "editable"],
      defaultValue: ["editable"],
    },
    label: {
      type: MarketplaceFormElementType.Text,
      required: true,
    },
    tooltip: {
      type: MarketplaceFormElementType.Text,
      widget: MarketplaceFormElementWidget.Textarea,
    },
    widget: {
      type: MarketplaceFormElementType.Select,
      optionType: MarketplaceOptionType.Text,
      optionValues: enumToOptions(MarketplaceFormElementWidget),
      hideIf: "!['SELECT', 'MULTI_SELECT', 'TEXT', 'DATE'].includes(type)",
      required: false,
    },
    grid: {
      type: MarketplaceFormElementType.Select,
      optionType: MarketplaceOptionType.Text,
      optionValues: enumToOptions(MarketplaceOptionGridType),
      hideIf: "!['SELECT', 'MULTI_SELECT'].includes(type)",
    },
    validation: {
      type: MarketplaceFormElementType.Select,
      optionType: MarketplaceOptionType.Text,
      optionValues: ["EMAIL"],
      hideIf: "type !== 'TEXT'",
    },
    regex: {
      type: MarketplaceFormElementType.Text,
      hideIf: "type !== 'TEXT'",
    },
    description: {
      type: MarketplaceFormElementType.Text,
      widget: MarketplaceFormElementWidget.Textarea,
    },
    optionType: {
      type: MarketplaceFormElementType.Select,
      optionType: MarketplaceOptionType.Text,
      optionValues: enumToOptions(MarketplaceOptionType),
      hideIf: "!['SELECT', 'MULTI_SELECT', 'RADIO_BUTTON'].includes(type)",
    },
    optionValues: {
      type: MarketplaceFormElementType.Text,
      widget: MarketplaceFormElementWidget.Textarea,
      hideIf: "!['SELECT', 'MULTI_SELECT', 'RADIO_BUTTON'].includes(type)",
    },
    dateOptionValues: {
      type: MarketplaceFormElementType.Text,
      hideIf: "type !== 'DATE'",
      tooltip:
        "This field is used to validate dates. Acceptable values are one of the following: \n\nincludeToday\nbusinessDays:#\ndays:#\n\nwhere # is an integer",
    },
    min: {
      type: MarketplaceFormElementType.Number,
      hideIf: "type !== 'NUMBER'",
    },
    max: {
      type: MarketplaceFormElementType.Number,
      hideIf: "type !== 'NUMBER'",
    },
    hideIf: {
      type: MarketplaceFormElementType.Text,
    },
    placeholder: {
      type: MarketplaceFormElementType.Text,
    },
    role: {
      type: MarketplaceFormElementType.Select,
      optionType: MarketplaceOptionType.Text,
      defaultValue: MarketplaceFormElementRole.None,
      optionValues: enumToOptions(MarketplaceFormElementRole),
      required: true,
    },
  };

const widgetMap: { [key: string]: MarketplaceFormElementWidget[] } = {
  [MarketplaceFormElementType.Text]: [
    MarketplaceFormElementWidget.ShortPlainText,
    MarketplaceFormElementWidget.Textarea,
    MarketplaceFormElementWidget.Phone,
  ],
  [MarketplaceFormElementType.Select]: [
    MarketplaceFormElementWidget.Checkboxes,
  ],
  [MarketplaceFormElementType.MultiSelect]: [
    MarketplaceFormElementWidget.Checkboxes,
  ],
};

interface GenericFormTypes {
  name: string;
  partialElements: Partial<MarketplaceFormElement>[];
}

export const scrollToErrors = (forms: Form<any>[]): void => {
  const formWithError = forms.find(formHasErrors) as any;
  if (formWithError) {
    const name = formWithError.state?.errors[0].property.substring(1);
    const element =
      formWithError.formElement.querySelector(`.key__${name}`) ||
      formWithError.formElement;

    if (element) {
      // Find the first input/textarea/select that has a FormMessage error indicator
      let scrollTarget = element;

      // Look for fieldset or use the element itself
      const fieldset = element.querySelector("fieldset");

      const searchContainer = fieldset || element;

      // Find all input elements
      const inputs = searchContainer.querySelectorAll(
        'input, textarea, select, [role="combobox"], [role="textbox"]'
      );

      // Find the first input that has a FormMessage error indicator
      const inputWithError = Array.from<Element>(inputs).find(input => {
        const parent = input.closest(`.key__${name}`);
        if (parent) {
          // Check if this input's container has a FormMessage error
          const hasFormMessage = parent.querySelector('[class*="FormMessage"]');
          return !!hasFormMessage;
        }
        return false;
      });

      if (inputWithError) {
        scrollTarget = inputWithError;
      }

      scrollTarget.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "center",
      });
    }
  }
};

export const formHasErrors = (form: Form<any>) => {
  return _.get(form, "state.errors.length", 0) > 0;
};

export const createGenericForm = ({
  name,
  partialElements,
}: GenericFormTypes): MarketplaceForm => {
  const elements = partialElements.map(e => ({
    ...defaultPropsForElements,
    ...e,
  }));
  const config = {
    elements,
    elementArrangement: elements.map(e => e.key),
    dependencies: null,
  } as MarketplaceFormConfig;
  const formDataSchema = schemaGenerator.generateSchema(name, config);

  return {
    id: v4(),
    versionId: v4(),
    title: name,
    icon: MarketplaceFormIcon.Cog,
    slug: "generic",
    name,
    description: "",
    config,
    formDataSchema,
    disableStaffEdit: false,
  };
};

export const getConfigurationEditorForm = () => {
  const elements: MarketplaceFormElement[] = [
    {
      ...defaultPropsForElements,
      key: "name",
      label: "Name",
      required: true,
    },
    {
      ...defaultPropsForElements,
      key: "description",
      label: "Description",
    },
    {
      ...defaultPropsForElements,
      key: "slug",
      label: "Slug",
    },
    {
      ...defaultPropsForElements,
      key: "icon",
      label: "Icon",
      type: MarketplaceFormElementType.Select,
      optionValues: enumToOptions(MarketplaceFormIcon),
    },
  ];
  const config = {
    elements,
    elementArrangement: elements.map(e => e?.key as string),
    dependencies: null,
  } as MarketplaceFormConfig;
  const formDataSchema = schemaGenerator.generateSchema("", config);

  return {
    id: v4(),
    versionId: v4(),
    title: "",
    icon: MarketplaceFormIcon.Cog,
    name: "",
    description: "",
    slug: null,
    config,
    formDataSchema,
    disableStaffEdit: false,
  };
};

export const getElementForm = (
  type: MarketplaceFormElementType = MarketplaceFormElementType.Text
): MarketplaceForm => {
  const elementArrangement: string[] = [];
  const elements = _.map(partialElementConfig, (val, key) => {
    elementArrangement.push(key);
    const overrides: any = {};
    if (key === "widget") {
      overrides.optionValues = widgetMap[type];
    }
    return {
      ...defaultPropsForElements,
      ...(val as any),
      ...overrides,
      key,
      label: _.startCase(key),
    };
  });

  const config = {
    elements,
    elementArrangement,
    dependencies: null,
  } as MarketplaceFormConfig;
  const formDataSchema = schemaGenerator.generateSchema("Element Form", config);

  return {
    id: v4(),
    versionId: v4(),
    title: "Element Form",
    icon: MarketplaceFormIcon.Cog,
    name: "Element Form",
    description: "",
    slug: "element_form",
    config,
    formDataSchema,
    disableStaffEdit: false,
  };
};

export const getElementSelectorForm = (
  elementList: string[]
): MarketplaceForm => {
  const elementArrangement: string[] = ["formElement"];
  const elements: MarketplaceFormElement[] = [
    {
      ...defaultPropsForElements,
      key: "formElement",
      label: "Selected Form Element",
      type: MarketplaceFormElementType.Select,
      optionValues: elementList.filter(i => !!i),
    },
  ];

  const config = {
    elements,
    elementArrangement,
    dependencies: null,
  } as MarketplaceFormConfig;
  const formDataSchema = schemaGenerator.generateSchema("", config);

  return {
    id: v4(),
    versionId: v4(),
    title: "",
    icon: MarketplaceFormIcon.Cog,
    name: "",
    description: "",
    slug: "selector_form",
    config,
    formDataSchema,
    disableStaffEdit: false,
  };
};

export const getNewForm = (): MarketplaceForm => {
  const elementArrangement: string[] = [];
  const elements: MarketplaceFormElement[] = [];
  const config = {
    elements,
    elementArrangement,
    dependencies: null,
  } as MarketplaceFormConfig;
  const formDataSchema = schemaGenerator.generateSchema("", config);
  return {
    id: v4(),
    versionId: v4(),
    title: "",
    icon: MarketplaceFormIcon.Cog,
    name: "",
    description: "",
    slug: "new_form",
    config,
    formDataSchema,
    disableStaffEdit: false,
  };
};

export const deepStrip = (obj: any, keys: string[]): any => {
  if (_.isArray(obj)) {
    return _.map(obj, i => deepStrip(i, keys));
  }
  if (_.isObject(obj)) {
    return _.chain(obj)
      .omit(keys)
      .mapValues(i => deepStrip(i, keys))
      .value();
  }
  return obj;
};

export const stripForm = (obj: any) =>
  deepStrip(obj, ["__typename", "clearWhenChanged"]);
